import * as React from 'react'
import { useState, useEffect } from 'react'
import {
  filterOption,
  getCategoryName,
  GrPrice,
  parseErrorMsg,
} from '../../../../../../../utilities/helpers'
import { TooltipWithIcon } from '../../../../../../../components/TooltipWithIcon'
import { Routes } from '../../../../../../../constants/Routes'
import {
  API,
  IProductFoundInProductListing,
  IBaseProduct,
  INestedProduct,
} from '@getgreenline/homi-shared'
import { SmartImage } from '../../../../../../../components/SmartImage'
import { Icon, Tooltip, message, Radio, Input, Select, Button } from 'antd'
import { ParentType } from '../../../../../DashboardProducts/AddProduct/VariantsSection/MergeProductModal'
import { CurrentCompanyStore } from '../../../../../../../stores/CurrentCompanyStore'
import { CreateProduct } from '../../../../../DashboardProducts/AddProduct/ProductStore'
import { Track } from '../../../../../../../Track'
import { ProductAsyncSelect } from '../../../../../../../components/ProductAsyncSelect'
import { isNil } from 'lodash'

interface Props {
  product: IProductFoundInProductListing
  productNameId: string
  companyId: number
  currentCompanyStore: CurrentCompanyStore
}

const INPUT_WIDTH = '12.5rem'
const INPUT_HEIGHT = '1.5rem'
const INPUT_FONTSIZE = '0.75rem'

// This component is only for admin users for now
export const POImportProductItemAdmin = (props: Props) => {
  const [isEditView, setEditView] = useState<boolean>(false)
  const [isRadioDisabled, setRadioDisabled] = useState<boolean>(false)
  const [parentType, setParentType] = useState<ParentType | null>(null)
  const [newParentName, setNewParentName] = useState<string>('')
  const [selectedCategoryId, setCategoryId] = useState<string | undefined>(undefined)
  const [selectedParentProduct, setParentProduct] = useState<IBaseProduct | undefined>(undefined)
  const createProduct = useState<CreateProduct>(new CreateProduct())[0]

  const { productNameId, product, companyId, currentCompanyStore } = props
  const { productDetail, caseCount, total, quantityPerCase, batchNumber } = product
  const { id: productId, parentProductId, sku, name } = productDetail
  const quantityDescription = `qty: ${caseCount} x ${quantityPerCase} units`

  const loadParentProduct = async (product: INestedProduct) => {
    const parentProduct = await API.getProductById(companyId, product.parentProductId!)
    setParentProduct(parentProduct)
    setParentType(ParentType.EXISTING)
  }

  const loadProduct = async () => {
    try {
      const product = await API.getProductById(companyId, productDetail.id)

      createProduct.populateFields(product)

      if (hasParentProduct()) {
        await loadParentProduct(product)
      } else {
        setParentProduct(undefined)
      }
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error finding your product')
    }
  }

  useEffect(() => {
    if (productDetail.categoryId) {
      setCategoryId(productDetail.categoryId)
    }

    loadProduct()
    setRadioDisabled(hasParentProduct())

    if (!productDetail.categoryId) {
      setEditView(true)
    }
  }, [])

  const updateProduct = async () => {
    const updateObject = createProduct.networkObject as any
    updateObject.id = createProduct.id

    try {
      const product = await currentCompanyStore!.productStore!.editProduct(
        companyId,
        productDetail.id,
        updateObject,
      )
      Track.track('dashboard:products_updated', product)
      message.success(`Successfully updated`)
      createProduct.populateFields(product)
      createProduct.unsetChanged()
      createProduct.needsPricingGroupRefresh = false
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error finding your product')
    }
  }

  const handleMerge = async (product: IBaseProduct | undefined) => {
    let parentProduct = product
    if (parentType === ParentType.NEW) {
      if (!newParentName) {
        return message.error('Please enter a new parent product name')
      }

      if (!selectedCategoryId) {
        return message.error('Please select a category')
      }

      parentProduct = await createParentProduct()
    }

    await mergeToParentProduct(parentProduct)

    setParentType(ParentType.EXISTING)
    setNewParentName('')
    setRadioDisabled(true)
  }

  const createParentProduct = async () => {
    try {
      const product = new CreateProduct()
      product.setName(newParentName)
      product.setCategoryId(selectedCategoryId || null)
      product.setSupplierId(productDetail.supplierId)
      product.setTaxGroupId(productDetail.taxGroupId)

      const createObject = product.networkObject
      const newParentProduct = await API.addProduct(companyId, createObject)
      setParentProduct(newParentProduct)
      setCategoryId(newParentProduct.categoryId || undefined)
      return newParentProduct
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error creating your new parent product')
    }
  }

  const mergeToParentProduct = async (product: IBaseProduct | undefined) => {
    try {
      if (product) {
        const newProduct = await API.mergeProduct(companyId, productDetail.id, product.id)
        message.success('Product successfully merged')
      }
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error converting this variant.')
    }
  }

  const renderCategoryDropdown = () => {
    const productStore = currentCompanyStore!.productStore!
    const flattenedCategories = productStore!.flattenedCategories || []

    return (
      <Select<string | null>
        showSearch
        className=''
        size='small'
        dropdownMatchSelectWidth={false}
        value={selectedCategoryId}
        allowClear
        onChange={(categoryId) => {
          setCategoryId(categoryId || undefined)
          if (parentType === null) {
            createProduct.setCategoryId(categoryId)
            updateProduct()
          }
        }}
        filterOption={filterOption}
        style={{
          width: INPUT_WIDTH,
          marginTop: '2px',
          fontSize: INPUT_FONTSIZE,
        }}
        placeholder={'Category'}
      >
        {flattenedCategories.map((category, index) => (
          <Select.Option key={index} value={category.id}>
            {getCategoryName(category)}
          </Select.Option>
        ))}
      </Select>
    )
  }

  const renderReadonlyCategoryInput = () => {
    const parentCategoryName =
      selectedParentProduct && selectedParentProduct.categoryName
        ? selectedParentProduct.categoryName
        : ''
    return (
      <div className='d-flex'>
        <Input
          value={parentCategoryName}
          readOnly={true}
          style={{
            width: INPUT_WIDTH,
            height: INPUT_HEIGHT,
            paddingLeft: '7px',
            marginTop: '2px',
            fontSize: INPUT_FONTSIZE,
          }}
          placeholder='Category Name'
        />
      </div>
    )
  }

  const renderParentDropdown = () => {
    return (
      <div>
        <ProductAsyncSelect
          placeholder='Select parent product'
          optionFilterFunction={(products) => {
            return products.filter((p) => p.isMasterProduct)
          }}
          value={selectedParentProduct ? selectedParentProduct.id : undefined}
          onSelect={(product) => {
            setParentProduct(product)
            handleMerge(product)
          }}
          size={'small'}
          style={{
            width: INPUT_WIDTH,
            height: INPUT_HEIGHT,
            fontSize: INPUT_FONTSIZE,
          }}
        />
      </div>
    )
  }

  const renderNewParentInput = () => {
    return (
      <div className='d-flex'>
        <Input
          value={newParentName}
          placeholder='New parent name'
          onChange={(e) => setNewParentName(e.target.value)}
          style={{
            width: INPUT_WIDTH,
            height: INPUT_HEIGHT,
            paddingLeft: '7px',
            fontSize: INPUT_FONTSIZE,
          }}
        />
        <Button
          onClick={() => handleMerge(undefined)}
          className='ml-1'
          style={{ height: INPUT_HEIGHT }}
        >
          Save
        </Button>
      </div>
    )
  }

  const hasParentProduct = () => {
    return !productDetail.isMasterProduct && !isNil(productDetail.parentProductId)
  }

  return (
    <li key={`${productNameId}_${productDetail.id}`} className='row ml-0 mr-0 mb-4 pl-3'>
      <header className='d-flex justify-content-center product-list__header p-0 col-2'>
        <SmartImage
          className='rounded'
          src={productDetail.imageUrl}
          width={122}
          customStyles={{
            maxWidth: 'none',
          }}
        />
      </header>

      <main className='col-10 pr-0' style={{ paddingLeft: '32px' }}>
        <a
          className='product-list__link cursor-pointer'
          href={Routes.EDIT_PRODUCT(companyId, parentProductId || productId)}
          target='_blank'
          rel='noreferrer'
        >
          <b
            className='product-name'
            data-tip
            data-for={productNameId}
            style={{ fontSize: '0.9rem' }}
          >
            {!productDetail.categoryId && (
              <Tooltip
                placement={'top'}
                title={
                  <div style={{ textAlign: 'center' }}>This product does not have a category.</div>
                }
              >
                <Icon
                  className='mr-2'
                  type='exclamation-circle'
                  style={{ color: 'red', fontSize: '0.9rem' }}
                />
              </Tooltip>
            )}

            {name}

            <TooltipWithIcon
              tooltipId={productNameId}
              place='bottom'
              effect='float'
              tooltipIconClassName='fa fa-external-link product-list__external-link ml-2'
              tooltipContent='Click to open product page'
            />
          </b>
        </a>

        {caseCount === 0 && (
          <div className='product-warning d-flex align-items-center' style={{ fontSize: '11px' }}>
            <Tooltip title='AGLC did not ship this product. It will not be imported to your purchase order.'>
              <Icon className='mr-2' type='exclamation-circle' />
            </Tooltip>

            <span>product not shipped</span>
          </div>
        )}

        <div className='product-list__description'>
          <small className='d-flex'>
            <b>{`Total: ${GrPrice.renderDollarString(total)}`}</b>
            <div className='ml-2'>{`${sku} | ${quantityDescription}`}</div>
            {batchNumber && (
              <div className='ml-1'>
                | Lot number: <b>{batchNumber}</b>
              </div>
            )}
            {!isEditView && !hasParentProduct() && (
              <a
                className='ml-auto mr-5'
                onClick={() => setEditView(true)}
                style={{ fontSize: '13px' }}
              >
                Edit
              </a>
            )}
          </small>
        </div>

        {isEditView ? (
          <div>
            <div>
              <b style={{ fontSize: '13px' }}>Assign parent product:</b>
            </div>

            <div className='d-flex'>
              <div>
                <Radio.Group
                  onChange={(e) => setParentType(e.target.value)}
                  value={parentType}
                  style={{
                    // antd doesn't support a way to customize the radio button size other than 'small/medium/large'
                    transform: 'scale(0.8)',
                    marginLeft: '-18px',
                    marginRight: '-10px',
                    marginTop: '-6px',
                    marginBottom: '-10px',
                  }}
                  className='d-flex flex-column ml-0 mr-2'
                  size='small'
                >
                  <Radio className='ml-3' value={ParentType.EXISTING}>
                    Existing
                  </Radio>
                  <Radio className='ml-3' value={ParentType.NEW}>
                    New
                  </Radio>
                  <Radio className='ml-3' value={null} disabled={isRadioDisabled}>
                    None
                  </Radio>
                </Radio.Group>
              </div>
              <div
                className='d-flex flex-column justify-content-end ml-5'
                style={{ height: '3.125rem' }}
              >
                {parentType === ParentType.EXISTING && (
                  <>
                    {renderParentDropdown()}
                    {renderReadonlyCategoryInput()}
                  </>
                )}

                {parentType === ParentType.NEW && (
                  <>
                    {renderNewParentInput()}
                    {renderCategoryDropdown()}
                  </>
                )}

                {parentType === null && renderCategoryDropdown()}
              </div>
            </div>
          </div>
        ) : (
          <div className='d-flex align-items-center justify-content-start mt-3'>
            <div className='ml-3'>
              <div style={{ fontSize: INPUT_FONTSIZE }}>Parent Product</div>
              <b>{productDetail.parentProductName || 'None'}</b>
            </div>
            <div className='ml-5'>
              <div style={{ fontSize: INPUT_FONTSIZE }}>Category</div>
              <b>{productDetail.categoryName || 'None'}</b>
            </div>
          </div>
        )}
      </main>
    </li>
  )
}
